// apps/spa/src/components/layout/OfficerMenu.tsx
// Avatar + name dropdown in the right of the header. Logout flow:
//   POST /api/auth/logout -> queryClient.clear() -> hard redirect to /login.
//
// Per RESEARCH Pitfall 4: clear caches AND hard-reload to nuke React state —
// soft-navigating away from a logged-in tree leaves stale TanStack Query cache.

import { useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Clock, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useRosterExport } from '@/hooks/useRosterExport';
import { useTimeFormat } from '@/hooks/useTimeFormat';
import { api } from '@/lib/api';

function initialsFor(username: string | undefined): string {
  if (!username) return '??';
  const parts = username.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0]![0] + parts[parts.length - 1]![0]).toUpperCase();
  }
  return username.slice(0, 2).toUpperCase();
}

function avatarUrlFor(
  user: { discordUserId: string; avatar: string | null } | undefined,
): string | undefined {
  if (!user || !user.avatar) return undefined;
  return `https://cdn.discordapp.com/avatars/${user.discordUserId}/${user.avatar}.png?size=64`;
}

export function OfficerMenu() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const rosterExport = useRosterExport();
  const time = useTimeFormat();

  async function handleLogout() {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore network failure — proceed with cache clear + hard reload anyway.
    }
    queryClient.clear();
    window.location.href = '/login';
  }

  const avatarUrl = avatarUrlFor(user);
  const initials = initialsFor(user?.username);

  return (
    <div data-testid="officer-menu" className="inline-flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Officer menu"
          className="inline-flex items-center gap-2 rounded-full px-1 py-1 text-sm transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Avatar size="sm">
            {avatarUrl ? <AvatarImage src={avatarUrl} alt={user?.username ?? ''} /> : null}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden text-sm font-medium text-foreground sm:inline">
            {user?.username ?? '...'}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8}>
          {/* Server-side specialistGate enforces the role check; non-specialists who
              click get redirected to /auth/role-required (consistent with all other
              role-gated actions). Visible to all logged-in users by design. */}
          <DropdownMenuItem
            disabled={rosterExport.isPending}
            onClick={() => rosterExport.mutate()}
          >
            {rosterExport.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <ClipboardList />
            )}
            Run roster export
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => time.toggle()}>
            <Clock />
            Switch to {time.format === '12h' ? '24-hour' : '12-hour'} time
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
